import React, { useState } from 'react';
import { apiService } from '../../services/apiService';
import { notificationService } from '../../services/notificationService';

interface ForgotPasswordProps {
  onBackToLogin: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      notificationService.warning('Please enter your email address');
      return;
    }

    setIsLoading(true);

    try {
      // Get the current window URL to determine callback URL
      const currentUrl = window.location.origin;
      const callbackUrl = `${currentUrl}/reset-password`;

      await apiService.post('/auth/password-reset-request', { 
        email,
        callback_url: callbackUrl
      });
      
      setIsSubmitted(true);
      notificationService.success('If an account with that email exists, we have sent you a password reset link.');
      
    } catch (error: any) {
      console.error('Password reset request error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to send reset email. Please try again.';
      notificationService.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <style>
          {`
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes float {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-10px); }
            }
            .success-container {
              animation: fadeInUp 0.8s ease-out;
            }
            .floating-icon {
              animation: float 3s ease-in-out infinite;
            }
          `}
        </style>
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '1rem',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background decorative elements */}
          <div style={{
            position: 'absolute',
            top: '10%',
            left: '10%',
            width: '100px',
            height: '100px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            animation: 'float 4s ease-in-out infinite'
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '15%',
            right: '15%',
            width: '150px',
            height: '150px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '50%',
            animation: 'float 6s ease-in-out infinite reverse'
          }}></div>

          <div className="success-container" style={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            boxShadow: '0 32px 64px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            padding: '3rem 2.5rem',
            width: '100%',
            maxWidth: '420px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            position: 'relative',
            textAlign: 'center'
          }}>
            <div className="floating-icon" style={{ 
              marginBottom: '1.5rem',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              boxShadow: '0 8px 32px rgba(16, 185, 129, 0.4)',
              position: 'relative'
            }}>
              <i className="bi bi-check-circle" style={{ fontSize: '2.5rem', color: 'white' }}></i>
              <div style={{
                position: 'absolute',
                inset: '-2px',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                borderRadius: '22px',
                zIndex: -1,
                opacity: 0.3,
                filter: 'blur(8px)'
              }}></div>
            </div>

            <h1 style={{ 
              color: '#1a202c', 
              fontWeight: 700, 
              marginBottom: '1rem',
              fontSize: '1.75rem',
              letterSpacing: '-0.025em'
            }}>Check Your Email</h1>

            <p style={{ 
              color: '#64748b', 
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '1.6',
              marginBottom: '2rem'
            }}>
              We've sent a password reset link to <strong style={{ color: '#374151' }}>{email}</strong>. 
              Please check your email and follow the instructions to reset your password.
            </p>

            <div style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '2rem'
            }}>
              <p style={{ 
                color: '#64748b', 
                fontSize: '0.875rem',
                margin: 0,
                lineHeight: '1.5'
              }}>
                <i className="bi bi-info-circle" style={{ marginRight: '0.5rem', color: '#667eea' }}></i>
                Didn't receive the email? Check your spam folder or try again in a few minutes.
              </p>
            </div>

            <button
              type="button"
              style={{
                width: '100%',
                padding: '1rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                marginBottom: '1rem',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                letterSpacing: '0.025em'
              }}
              onClick={onBackToLogin}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
              }}
            >
              <i className="bi bi-arrow-left" style={{ marginRight: '0.75rem' }}></i>
              Back to Login
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .forgot-container {
            animation: fadeInUp 0.8s ease-out;
          }
          .floating-icon {
            animation: float 3s ease-in-out infinite;
          }
        `}
      </style>
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '1rem',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decorative elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '100px',
          height: '100px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          animation: 'float 4s ease-in-out infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '15%',
          right: '15%',
          width: '150px',
          height: '150px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite reverse'
        }}></div>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '5%',
          width: '80px',
          height: '80px',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '50%',
          animation: 'float 5s ease-in-out infinite'
        }}></div>

        <div className="forgot-container" style={{
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          boxShadow: '0 32px 64px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
          padding: '3rem 2.5rem',
          width: '100%',
          maxWidth: '420px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          position: 'relative'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <div className="floating-icon" style={{ 
              marginBottom: '1.5rem',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              boxShadow: '0 8px 32px rgba(245, 158, 11, 0.4)',
              position: 'relative'
            }}>
              <i className="bi bi-key" style={{ fontSize: '2.5rem', color: 'white' }}></i>
              <div style={{
                position: 'absolute',
                inset: '-2px',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                borderRadius: '22px',
                zIndex: -1,
                opacity: 0.3,
                filter: 'blur(8px)'
              }}></div>
            </div>
            <h1 style={{ 
              color: '#1a202c', 
              fontWeight: 700, 
              marginBottom: '0.5rem',
              fontSize: '2rem',
              letterSpacing: '-0.025em',
              background: 'linear-gradient(135deg, #1a202c 0%, #374151 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Reset Password</h1>
            <p style={{ 
              color: '#64748b', 
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '1.5'
            }}>Enter your email to receive a reset link</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '2rem' }}>
              <label htmlFor="email" style={{ 
                display: 'block', 
                fontSize: '0.875rem', 
                fontWeight: 600, 
                color: '#374151', 
                marginBottom: '0.75rem',
                letterSpacing: '0.025em'
              }}>
                <i className="bi bi-envelope" style={{ marginRight: '0.5rem', color: '#f59e0b' }}></i>
                Email Address
              </label>
              <input
                type="email"
                id="email"
                style={{
                  width: '100%',
                  padding: '1rem 1.25rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  background: '#fafbfc',
                  fontWeight: 400
                }}
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#f59e0b';
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = '#fafbfc';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            <button
              type="submit"
              style={{
                width: '100%',
                padding: '1rem',
                background: isLoading ? '#e5e7eb' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: isLoading ? '#64748b' : 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                marginBottom: '1.5rem',
                boxShadow: isLoading ? 'none' : '0 4px 15px rgba(245, 158, 11, 0.4)',
                letterSpacing: '0.025em'
              }}
              disabled={isLoading}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(245, 158, 11, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(245, 158, 11, 0.4)';
                }
              }}
            >
              {isLoading ? (
                <>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid #e5e7eb',
                    borderTop: '2px solid #64748b',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    display: 'inline-block',
                    marginRight: '0.75rem'
                  }}></div>
                  Sending Reset Link...
                </>
              ) : (
                <>
                  <i className="bi bi-envelope" style={{ marginRight: '0.75rem' }}></i>
                  Send Reset Link
                </>
              )}
            </button>
          </form>

          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              style={{
                background: 'none',
                border: 'none',
                color: '#667eea',
                fontSize: '0.875rem',
                cursor: 'pointer',
                textDecoration: 'none',
                fontWeight: 600,
                transition: 'color 0.2s ease'
              }}
              onClick={onBackToLogin}
              onMouseEnter={(e) => e.currentTarget.style.color = '#5a67d8'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#667eea'}
            >
              <i className="bi bi-arrow-left" style={{ marginRight: '0.5rem' }}></i>
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;